import { useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledUTF8BOMWarning,
  StyledFilePickerNote,
  StyledConnectionButtonsRow,
} from './styles';
import FilePicker from 'components/FilePicker';
import { useProgress } from '__hooks/uploadFiles/useProgress';
import { useUploadFile } from '__hooks/uploadFiles/useUploadFile';
import { useFiles } from '__hooks/uploadFiles/useFiles';
import {
  ALLOWED_SOURCE_FILE_EXTENSIONS,
  ALLOWED_WEBHOOK_SOURCE_FILE_EXTENSIONS,
  SMART_CONNECTOR_TYPES,
  SMARTCONNECTORS_S3_SOURCES,
} from 'pages/SmartConnectors/constants';
import { ScriptFile } from 'pages/SmartConnectors/types';
import { useUTF8BOMWarning } from 'pages/SmartConnectors/hooks/useUTF8BOMWarning';
import { SmartConnectorContext } from '../../context';
import { DownloadTemplateButton } from './DownloadTemplateButton';
import { WithTooltip } from 'components/Inputs/inline/WithTooltip';
import Button from '__components/Button';
import { downloadFile } from 'pages/SmartConnectors/helpers';
import { KizenTypography } from '__app/typography';
import { CreateWebhookButton } from './CreateWebhookButton';

type FileUploaderProps = {
  onChange: (file: ScriptFile) => Promise<void>;
};

export const FileUploader = ({ onChange }: FileUploaderProps) => {
  const { t } = useTranslation();
  const { stepData, smartConnector } = useContext(SmartConnectorContext);
  const isWebhook =
    smartConnector.connector_type === SMART_CONNECTOR_TYPES.webhook;

  const [showUTF8Warning, checkUTF8Warning] = useUTF8BOMWarning();

  const [progress, { clearProgress, initializeProgress, updateProgress }] =
    useProgress();

  const uploadFile = useUploadFile(
    false,
    updateProgress,
    SMARTCONNECTORS_S3_SOURCES.IMPORT
  );

  const [, { addFiles }] = useFiles({
    uploadFile,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddFiles = useCallback(
    async (updatedFiles: File[]) => {
      checkUTF8Warning(updatedFiles[0]);
      initializeProgress(updatedFiles);
      const createdFiles = await addFiles(updatedFiles);
      if (createdFiles.length) {
        await onChange(createdFiles[0]);
      }
      clearProgress();
    },
    [addFiles, clearProgress, onChange, initializeProgress, checkUTF8Warning]
  );

  const uploadStatus = Object.values(
    (progress || { progress: 0 }) as Record<string, { progress: number }>
  )[0].progress;

  const handleDownloadSourceFile = () => {
    if (stepData.source_file?.url) {
      downloadFile(stepData.source_file.url);
    }
  };

  return (
    <>
      <KizenTypography>{t('Upload Sample File')}</KizenTypography>
      <StyledConnectionButtonsRow>
        {isWebhook ? (
          <CreateWebhookButton fileInputRef={fileInputRef} />
        ) : (
          <DownloadTemplateButton
            customObjectId={smartConnector.custom_object}
          />
        )}
        {stepData.source_file ? (
          <WithTooltip
            showToolTip
            tooltipLabel={stepData.source_file.name}
            popperConfig={{
              modifiers: {
                offset: { offset: '0, -14' },
                preventOverflow: { boundariesElement: 'viewport' },
              },
            }}
          >
            <Button
              variant="text"
              color="blue"
              onClick={handleDownloadSourceFile}
            >
              {t('Download Current Sample File')}
            </Button>
          </WithTooltip>
        ) : null}
      </StyledConnectionButtonsRow>
      <FilePicker
        onFileAdded={handleAddFiles}
        allowedFileExtensions={
          isWebhook
            ? ALLOWED_WEBHOOK_SOURCE_FILE_EXTENSIONS
            : ALLOWED_SOURCE_FILE_EXTENSIONS
        }
        uploadStatus={uploadStatus}
        disabled={!!progress}
        inputRef={fileInputRef}
      />
      <StyledFilePickerNote>
        {isWebhook
          ? t(
              'Only Webhook CSV files are supported - please make sure your file name ends with .csv or .zip'
            )
          : t(
              '*Only spreadsheet files are supported — please make sure your file name ends with .csv, .dat, .tsv, .xls, .xlsx, .xlsm, .xlsb, .ods or .zip'
            )}
      </StyledFilePickerNote>
      {showUTF8Warning ? (
        <StyledUTF8BOMWarning data-qa="utf8-bom-warning">
          {t(
            'A possible non-UTF-8 file was detected. Proceed with caution, as this may cause processing issues. For best results, ensure the file is UTF-8 encoded'
          )}
        </StyledUTF8BOMWarning>
      ) : null}
    </>
  );
};
